import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  getDocumentosInteres,
  getGlosario,
  getPreguntasFrecuentes,
  getRecursosEducativos,
} from "../../features/services/siteContentService";
import { normalizeAdminRichText } from "../../utils/richText";
import FaqPanel from "./FaqPanel";
import GlosarioPanel from "./GlosarioPanel";
import ResourceListPanel from "./ResourceListPanel";
import "./SiteContentModal.css";

const MODAL_CONFIG = {
  faq: {
    title: "Preguntas frecuentes",
    icon: "bi-question-circle",
    fetch: getPreguntasFrecuentes,
  },
  documentos: {
    title: "Documentos de interés",
    icon: "bi-journal-text",
    fetch: getDocumentosInteres,
  },
  glosario: {
    title: "Glosario",
    icon: "bi-book",
    fetch: getGlosario,
  },
  recursos: {
    title: "Recursos educativos",
    icon: "bi-mortarboard",
    fetch: getRecursosEducativos,
  },
};

function renderContent(type, data) {
  switch (type) {
    case "glosario":
      return <GlosarioPanel data={data} />;
    case "faq":
      return <FaqPanel data={data} />;
    case "documentos":
      return (
        <ResourceListPanel
          data={data}
          itemsKey="documentos"
          emptyLabel="No hay documentos disponibles."
        />
      );
    case "recursos":
      return (
        <ResourceListPanel
          data={data}
          itemsKey="recursos"
          emptyLabel="No hay recursos educativos disponibles."
        />
      );
    default:
      return null;
  }
}

export default function SiteContentModal({ type, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const config = type ? MODAL_CONFIG[type] : null;
  const open = Boolean(type && config);

  const loadContent = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await config.fetch();
      setData(result);
    } catch (err) {
      setError(err?.message || "No se pudo cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!open) return;
    loadContent();
  }, [open, loadContent]);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const intro = String(data?.introduccion ?? "").trim();

  return createPortal(
    <div className="scm-overlay" role="presentation" onClick={onClose}>
      <div
        className="scm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scm-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="scm-modal__header">
          <div className="scm-modal__title-wrap">
            <span className="scm-modal__icon" aria-hidden>
              <i className={`bi ${config.icon}`} />
            </span>
            <h2 id="scm-modal-title" className="scm-modal__title">
              {config.title}
            </h2>
          </div>
          <button
            type="button"
            className="scm-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <i className="bi bi-x-lg" aria-hidden />
          </button>
        </header>

        <div className="scm-modal__content">
          <div className="scm-modal__body">
            {loading ? (
              <div className="scm-modal__status">
                <div className="scm-spinner" aria-hidden />
                <p>Cargando contenido…</p>
              </div>
            ) : error ? (
              <div className="scm-modal__status scm-modal__status--error">
                <p>{error}</p>
                <button
                  type="button"
                  className="scm-retry-btn"
                  onClick={loadContent}
                >
                  Reintentar
                </button>
              </div>
            ) : (
              <>
                {intro ? (
                  <div
                    className="scm-modal__intro rich-text"
                    dangerouslySetInnerHTML={{
                      __html: normalizeAdminRichText(intro),
                    }}
                  />
                ) : null}
                {renderContent(type, data)}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export { MODAL_CONFIG };
